import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Carousel, Modal } from 'react-bootstrap';
import Form from "react-bootstrap/Form";
import Nav from 'react-bootstrap/Nav';
import Tab from 'react-bootstrap/Tab';
import Table from "react-bootstrap/Table";
import { toast } from 'react-toastify';
import CustomFormInput from '../components/CustomFormInput';
import CustomPagination from '../components/CustomPagination';
import Loader from '../components/Loader';
import ScrollingTab from '../components/ScrollingTab';
import { companyListing, crewListing, deleteFlaggedActivity, editActivity, editComment, getAlLReplyComment, getUserList, addMembers, getAllCategoryList, getAllActivityList } from '../controller';
import { requestHandler } from '../helper';
import { formatDateAsItIs, formatDate } from '../helper/common';
import { imagePath } from '../helper/image';
import Layout from '../layout/Layout';
import timeZoneData from '../helper/timeZoneData.json';

function MarketplaceManagement() {
    const navigate = useNavigate();

    const [crewData, setCrewData] = useState([]);
    const [categories, setCategories] = useState([]);
    const [companyData, setCompanyData] = useState([]);
    const [selectedActions, setSelectedActions] = useState({});
    const [activeTab, setActiveTab] = useState("crews");
    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState("");
    const [eventDate, setEventDate] = useState("");
    const [category, setCategory] = useState("");
    const [eventType, setEventType] = useState("");
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingAction, setPendingAction] = useState({ id: null, action: null });
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImages, setSelectedImages] = useState([]);

    // User selection modal state
    const [showUserModal, setShowUserModal] = useState(false);
    const [userModalData, setUserModalData] = useState({ id: null, type: null });
    const [userList, setUserList] = useState([]);
    const [userSearch, setUserSearch] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [userPagination, setUserPagination] = useState({
        page: 1,
        limit: 10,
        totalData: 0,
        totalPages: 0
    });
    const [userLoading, setUserLoading] = useState(false);

    const handleButtonClickActivity = (id, action) => {
        setPendingAction({ id, action });
        setShowConfirmModal(true);
    };

    //activity
    const [paginationValue, setPaginationValue] = useState({
        page: 1,
        limit: 10,
        totalData: 0,
        totalPages: 0
    })

    const { page, limit, totalData, totalPages } = paginationValue
    const effectiveLimit = (limit === "" || limit === "All") ? totalData : parseInt(limit);
    const start = totalData > 0 ? (page - 1) * effectiveLimit + 1 : 0;
    const end = Math.min(page * effectiveLimit, totalData);

    //comment
    const [paginationCommentValue, setPaginationCommentValue] = useState({
        page: 1,
        limit: 10,
        totalData: 0,
        totalPages: 0
    })

    // Fetch categories on component mount
    useEffect(() => {
        const fetchCategories = async () => {
            await requestHandler(
                async () => await getAllActivityList(),
                null,
                (res) => {
                    const categoryData = res.data || [];
                    setCategories([{ id: "", title: "All Categories" }, ...categoryData]);
                },
                () => { }
            );
        };
        fetchCategories();
    }, []);

    const getActivityData = async (page = 1, customLimit) => {
        let limitToUse = customLimit ?? paginationValue.limit ?? 10;
        // Handle "All" option - use a large number
        if (limitToUse === "" || limitToUse === "All") {
            limitToUse = 10000;
        }
        await requestHandler(
            async () => await crewListing(page, limitToUse, search, category),
            setLoading,
            (res) => {
                const { data, limit, page, totalData, totalPages } = res.data;
                setCrewData(data);
                setPaginationValue({
                    page: page ?? paginationValue.page ?? 1,
                    limit: limit ?? limitToUse,
                    totalData: totalData ?? paginationValue.totalData ?? 0,
                    totalPages: totalPages ?? paginationValue.totalPages ?? 0
                })
            },
            () => { }
        );
    };

    const { page: companyPage, limit: companyLimit, totalData: companyTotal, totalPages: companyTotalPages } = paginationCommentValue;
    const effectiveCompanyLimit = (companyLimit === "" || companyLimit === "All") ? companyTotal : parseInt(companyLimit);
    const companyStart = companyTotal > 0 ? (companyPage - 1) * effectiveCompanyLimit + 1 : 0;
    const companyEnd = Math.min(companyPage * effectiveCompanyLimit, companyTotal);

    const getCommentData = async (page = 1, customLimit) => {
        let limitToUse = customLimit ?? paginationCommentValue.limit ?? 10;
        // Handle "All" option - use a large number
        if (limitToUse === "" || limitToUse === "All") {
            limitToUse = 10000;
        }
        // Get selected category name
        const selectedCategory = categories.find(cat => cat.id === category);
        const categoryName = selectedCategory ? selectedCategory.title : "";
        let eventTypeValue = eventType;
        if (eventTypeValue) {
            eventTypeValue = eventTypeValue === "public" ? 1 : 0;
        }
        await requestHandler(
            async () => await companyListing(page, limitToUse, search, '', eventDate, categoryName, eventTypeValue),
            setLoading,
            (res) => {
                const { data, limit, page, totalData, totalPages } = res.data;
                setCompanyData(data);
                setPaginationCommentValue({
                    page: page ?? paginationCommentValue.page ?? 1,
                    limit: limit ?? limitToUse,
                    totalData: totalData ?? paginationCommentValue.totalData ?? 0,
                    totalPages: totalPages ?? paginationCommentValue.totalPages ?? 0
                })
            },
            () => { }
        );
    };

    useEffect(() => {
        if (activeTab === "crews") {
            if (search) {
                const delayDebounce = setTimeout(() => {
                    getActivityData(1, paginationValue.limit);
                }, 500);
                return () => clearTimeout(delayDebounce);
            } else {
                getActivityData(1, paginationValue.limit);
            }
        }
        if (activeTab === "company") {
            if (search) {
                const delayDebounce = setTimeout(() => {
                    getCommentData(1, paginationCommentValue.limit);
                }, 500);
                return () => clearTimeout(delayDebounce);
            } else {
                getCommentData(1, paginationCommentValue.limit);
            }
        }

        // eslint-disable-next-line
    }, [paginationValue.limit, search, eventDate, category, eventType, activeTab]);

    useEffect(() => {
        setSearch("");
        setEventDate("");
        setCategory("");
        setEventType("");
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === "company") {
            setEventDate("");
            setEventType("");
        }
        // eslint-disable-next-line
    }, [activeTab]);

    const handlePageChange = (page, type) => {
        if (type === 'crews') {
            setPaginationValue((prev) => ({
                ...prev,
                page,
            }));
            getActivityData(page, paginationValue.limit); // Update the page when changing page for crews tab
        } else if (type === 'company') {
            setPaginationCommentValue((prev) => ({
                ...prev,
                page,
            }))
            getCommentData(page, paginationCommentValue.limit); // Update the page when changing page for company tab

        }
    };

    // Function to handle image click
    const handleImageClick = (activityImage) => {
        if (activityImage.length > 0) {
            setSelectedImages(activityImage.map(img => img.url));
            setShowImageModal(true);
        }
    };

    // Function to get image URL
    const getImageUrl = (imgPath) => {
        if (!imgPath) return '';
        if (imgPath.startsWith('http://') || imgPath.startsWith('https://')) {
            return imgPath;
        }
        return `${imagePath()}${imgPath}`;
    };

    // Function to get timezone text by ID
    const getTimeZoneText = (timezoneId) => {
        if (!timezoneId) return '-';
        const timezone = timeZoneData.find(tz => tz.id === parseInt(timezoneId));
        return timezone ? timezone.text : timezoneId;
    };

    const handleCrewView = (crew) => {
        navigate('/event-details', { state: { eventData: crew } });
    };

    // User modal functions
    const openUserModal = (id, type) => {
        setUserModalData({ id, type });
        setSelectedUsers([]);
        setUserSearch('');
        setShowUserModal(true);
        fetchUserList(id, type, 1, '');
    };

    const fetchUserList = async (id, type, page = 1, search = '') => {
        await requestHandler(
            async () => await getUserList(id, type, page, 10, search),
            setUserLoading,
            (res) => {
                const { data, limit, page: resPage, totalData, totalPages } = res.data;
                setUserList(data || []);
                setUserPagination({
                    page: resPage || page,
                    limit: limit || 10,
                    totalData: totalData || 0,
                    totalPages: totalPages || 0
                });
            },
            () => { }
        );
    };

    // Debounce ref for user search
    const userSearchDebounceRef = useRef(null);

    const handleUserSearch = useCallback((searchValue) => {
        setUserSearch(searchValue);

        // Clear previous timeout
        if (userSearchDebounceRef.current) {
            clearTimeout(userSearchDebounceRef.current);
        }

        // Set new debounced search
        userSearchDebounceRef.current = setTimeout(() => {
            fetchUserList(userModalData.id, userModalData.type, 1, searchValue);
        }, 500);
    }, [userModalData.id, userModalData.type]);

    const handleUserPageChange = (page) => {
        setUserPagination(prev => ({ ...prev, page }));
        fetchUserList(userModalData.id, userModalData.type, page, userSearch);
    };

    const toggleUserSelection = (userId) => {
        setSelectedUsers(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
    };

    const toggleSelectAll = () => {
        const allUserIds = userList.map(user => user._id);
        const allSelected = allUserIds.every(id => selectedUsers.includes(id));
        if (allSelected) {
            setSelectedUsers(prev => prev.filter(id => !allUserIds.includes(id)));
        } else {
            setSelectedUsers(prev => [...new Set([...prev, ...allUserIds])]);
        }
    };

    return (
        <>
            {loading && <Loader />}
            <Layout>
                <div className="card-wrapper">
                    <div className="card-header border-bottom-0 pb-0">
                        <div className='theme-select'>
                            <Form.Select required name="limit" onChange={e => setPaginationValue({ ...paginationValue, [e.target.name]: e.target.value })}>
                                {/* <option value="">All</option> */}
                                <option value="10">10</option>
                                <option value="15">15</option>
                                <option value="20">20</option>
                            </Form.Select>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            {activeTab === "crews" && (
                                <div className="category-filter">
                                    <Form.Select
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value)}
                                        className="mb-0"
                                        style={{ height: '40px', padding: '8px 12px', borderRadius: '6px', width: '150px' }}
                                    >
                                        {categories.map((cat) => (
                                            <option key={cat.id} value={cat.id}>
                                                {cat.title}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </div>
                            )}
                            {activeTab === "company" && (
                                <div className="date-input">
                                    <CustomFormInput
                                        type="date"
                                        placeholder="Filter by event date"
                                        id="eventDate"
                                        className="mb-0"
                                        value={eventDate}
                                        name='eventDate'
                                        onChange={(e) => setEventDate(e.target.value)}
                                    />
                                </div>
                            )}
                            {activeTab === "company" && (
                                <div className="event-type-filter">
                                    <Form.Select
                                        value={eventType}
                                        onChange={(e) => setEventType(e.target.value)}
                                        className="mb-0"
                                        style={{ height: '40px', padding: '8px 12px', borderRadius: '6px', width: '150px' }}
                                    >
                                        <option value="">Select Type</option>
                                        <option value="public">Public</option>
                                        <option value="private">Private</option>
                                    </Form.Select>
                                </div>
                            )}
                            <div className="search-input">
                                <img src="/images/svg/search.svg" className="search-icon" alt="icon" />
                                <CustomFormInput
                                    type="search"
                                    placeholder="Search by name or owner name..."
                                    id="search"
                                    className="mb-0"
                                    value={search}
                                    name='search'
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="card-body">
                        <Tab.Container id="activityTab" activeKey={activeTab} onSelect={(key) => setActiveTab(key)}>
                            <ScrollingTab>
                                <Nav className='theme-tab'>
                                    <Nav.Item>
                                        <Nav.Link eventKey="crews">Crews</Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="company">Event Marketplace</Nav.Link>
                                    </Nav.Item>
                                </Nav>
                            </ScrollingTab>
                            <Tab.Content className='mt-3'>
                                <Tab.Pane eventKey="crews">
                                    <div className="overflow-x-auto theme-scrollbar">
                                        <Table className="theme-table">
                                            <thead>
                                                <tr>
                                                    <th>Crew Name</th>
                                                    <th>Owner</th>
                                                    <th>Category</th>
                                                    <th>Description</th>
                                                    <th>Time Zone</th>
                                                    <th>Created</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {crewData?.length > 0 ? (
                                                    crewData.map((row) => {
                                                        const owner = row?.ownerId;
                                                        const membersCount = Array.isArray(row?.members) ? row.members.length : 0;
                                                        return (
                                                            <tr key={row._id}>
                                                                <td style={{ minWidth: "180px" }}>{row?.name || "-"}</td>
                                                                <td>{owner ? `${owner.firstName || ""} ${owner.lastName || ""}`.trim() || owner.userName || "-" : "-"}</td>
                                                                <td>{row?.category || "-"}</td>
                                                                <td title={row?.description || "-"}>
                                                                    {(row?.description && row.description.length > 40) ? `${row.description.substring(0, 40)}...` : row?.description || "-"}
                                                                </td>
                                                                <td>{getTimeZoneText(row?.timeZone)}</td>
                                                                <td>{row?.createdAt ? formatDateAsItIs(row.createdAt) : "-"}</td>
                                                                <td width={120}>
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <button
                                                                            className="table-icon"
                                                                            onClick={() => handleCrewView(row)}
                                                                            title="View"
                                                                        >
                                                                            <img src="/images/svg/table-view.svg" alt="view" />
                                                                        </button>
                                                                        <button
                                                                            className="table-icon"
                                                                            onClick={() => openUserModal(row._id, 'crew')}
                                                                            title="Add Users"
                                                                        >
                                                                            <img src="/images/svg/invite-user.svg" alt="add users" />
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <tr>
                                                        <td colSpan={10} className="text-center p-5 fs-5">
                                                            No Records Found
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap mt-1">
                                        <p className="mb-0 fs-16 fw-500 text-light">
                                            Showing {start} to {end} of {totalData} entries
                                        </p>
                                        {totalPages > 1 && (
                                            <CustomPagination
                                                currentPage={page}
                                                totalPages={totalPages}
                                                onPageChange={(page) => handlePageChange(page, 'crews')}
                                            />
                                        )}
                                    </div>
                                </Tab.Pane>

                                <Tab.Pane eventKey="company">
                                    <div className='mt-3'>
                                                <div className="overflow-x-auto theme-scrollbar">
                                                    <Table className="theme-table">
                                                        <thead>
                                                            <tr>
                                                                <th>Event Name</th>
                                                                <th>Owner</th>
                                                                <th>Account Type</th>
                                                                <th>Crew</th>
                                                                {/* <th>Category</th> */}
                                                                <th>Event Date</th>
                                                                <th>Event Type</th>
                                                                <th>Location</th>
                                                                <th>Members</th>
                                                                <th>Co-hosts</th>
                                                                <th>Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {companyData?.length > 0 ? (
                                                                companyData.map((row) => {
                                                                    const owner = row?.ownerId;
                                                                    const membersCount = Array.isArray(row?.members) ? row.members.length : 0;
                                                                    // AO 2.0 (event hosting): accepted co-host Crews
                                                                    const coHostsCount = Array.isArray(row?.secondaryCrews) ? row.secondaryCrews.filter(h => h.status === 'accepted').length : 0;
                                                                    return (
                                                                        <tr key={row._id}>
                                                                            <td style={{ minWidth: "180px" }}>{row?.name || "-"}</td>
                                                                            <td style={{ minWidth: "160px" }}>
                                                                                {owner ? `${owner.firstName || ""} ${owner.lastName || ""}`.trim() || owner.userName || "-" : "-"}
                                                                            </td>
                                                                            <td>{owner?.role?.name || "-"}</td>
                                                                            <td style={{ minWidth: "140px" }}>{row?.primaryCrewId?.name || "-"}</td>
                                                                            {/* <td>{row?.category || "-"}</td> */}
                                                                            <td>{row?.eventDate ? formatDateAsItIs(row.eventDate) : "-"}</td>
                                                                            <td>{row?.eventType === 0 ? "Private" : row?.eventType === 1 ? "Public" : "-"}</td>
                                                                            <td style={{ minWidth: "180px" }} title={row?.location || "-"}>
                                                                                {(row?.location && row.location.length > 50) ? `${row.location.substring(0, 50)}...` : row?.location || "-"}
                                                                            </td>
                                                                            <td>{membersCount}/{row?.capacity ?? 0}</td>
                                                                            <td>{coHostsCount}</td>
                                                                            <td width={120}>
                                                                                <div className='d-flex align-items-center gap-2'>
                                                                                    <button
                                                                                        className="table-icon"
                                                                                        onClick={() => {
                                                                                            // setSelectedCompany(row);
                                                                                            // setShowCompanyModal(true);
                                                                                            navigate('/event-details', { state: { eventData: row } });
                                                                                        }}
                                                                                        title="View"
                                                                                    >
                                                                                        <img src="/images/svg/table-view.svg" alt="view" />
                                                                                    </button>
                                                                                    <button
                                                                                        className="table-icon"
                                                                                        onClick={() => openUserModal(row._id, 'company')}
                                                                                        title="Add Users"
                                                                                    >
                                                                                        <img src="/images/svg/invite-user.svg" alt="add users" />
                                                                                    </button>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan={10} className="text-center p-5 fs-5">
                                                                        No Records Found
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                                <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap mt-1">
                                                    <p className="mb-0 fs-16 fw-500 text-light">
                                                        Showing {companyStart} to {companyEnd} of {companyTotal} entries
                                                    </p>
                                                    {companyTotalPages > 1 && (
                                                        <CustomPagination
                                                            currentPage={companyPage}
                                                            totalPages={companyTotalPages}
                                                            onPageChange={(page) => handlePageChange(page, 'company')}
                                                        />
                                                    )}
                                                </div>
                                    </div>
                                </Tab.Pane>
                            </Tab.Content>
                        </Tab.Container>
                    </div>
                </div>
            </Layout>

            {/* Image Gallery Modal */}
            <Modal
                show={showImageModal}
                onHide={() => setShowImageModal(false)}
                centered
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Activity Images</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedImages.length > 0 && (
                        <Carousel>
                            {selectedImages.map((image, index) => (
                                <Carousel.Item key={index}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        minHeight: '400px',
                                        backgroundColor: '#f8f9fa'
                                    }}>
                                        <img
                                            className="d-block w-100"
                                            src={getImageUrl(image)}
                                            alt={`Activity ${index + 1}`}
                                            title={`Activity ${index + 1}`}
                                            style={{
                                                maxHeight: '500px',
                                                objectFit: 'contain',
                                                borderRadius: '8px'
                                            }}
                                        // onError={(e) => {
                                        //     e.target.src = '/images/placeholder.png';
                                        //     e.target.onerror = null;
                                        // }}
                                        />
                                    </div>
                                    {/* <Carousel.Caption>
                                        <p style={{ 
                                            backgroundColor: 'rgba(0,0,0,0.5)', 
                                            padding: '8px 16px',
                                            borderRadius: '4px',
                                            display: 'inline-block'
                                        }}>
                                            Image {index + 1} of {selectedImages.length}
                                        </p>
                                    </Carousel.Caption> */}
                                </Carousel.Item>
                            ))}
                        </Carousel>
                    )}
                </Modal.Body>
            </Modal>



            {/* User Selection Modal */}
            <Modal
                show={showUserModal}
                onHide={() => {
                    setShowUserModal(false);
                    setUserModalData({ id: null, type: null });
                    setSelectedUsers([]);
                    setUserSearch('');
                }}
                centered
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Select Users</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* Search */}
                    <div className="mb-3">
                        <div className="search-input position-relative">
                            <img src="/images/svg/search.svg" className="search-icon position-absolute" alt="icon" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                            <input
                                type="search"
                                className="form-control ps-5"
                                placeholder="Search users..."
                                value={userSearch}
                                onChange={(e) => handleUserSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Selected count */}
                    {selectedUsers.length > 0 && (
                        <div className="mb-3">
                            <span className="badge bg-light text-dark">{selectedUsers.length} user(s) selected</span>
                        </div>
                    )}

                    {/* User List */}
                    <div style={{ position: 'relative', minHeight: '200px' }}>
                        {userLoading && (
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 10,
                                borderRadius: '8px'
                            }}>
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        )}
                        <div className="overflow-x-auto theme-scrollbar" style={{ maxHeight: '400px' }}>
                            <Table className="theme-table">
                                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bs-body-bg)', zIndex: 1 }}>
                                    <tr>
                                        <th width={50}>
                                            <Form.Check
                                                className="ms-2"
                                                type="checkbox"
                                                checked={userList.length > 0 && userList.every(user => selectedUsers.includes(user._id))}
                                                onChange={toggleSelectAll}
                                                disabled={userLoading}
                                            />
                                        </th>
                                        <th>Name</th>
                                        <th>Username</th>
                                        <th>Email</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {userList?.length > 0 ? (
                                        userList.map((user) => (
                                            <tr
                                                key={user._id}
                                                onClick={() => !userLoading && toggleUserSelection(user._id)}
                                                style={{ cursor: userLoading ? 'default' : 'pointer' }}
                                                className={selectedUsers.includes(user._id) ? 'table-active' : ''}
                                            >
                                                <td>
                                                    <Form.Check
                                                        className="ms-2"
                                                        type="checkbox"
                                                        checked={selectedUsers.includes(user._id)}
                                                        onChange={() => toggleUserSelection(user._id)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        disabled={userLoading}
                                                    />
                                                </td>
                                                <td>{`${user.firstName || ''} ${user.lastName || ''}`.trim() || '-'}</td>
                                                <td>{user.userName || '-'}</td>
                                                <td>{user.email || '-'}</td>
                                            </tr>
                                        ))
                                    ) : !userLoading ? (
                                        <tr>
                                            <td colSpan={4} className="text-center p-4">
                                                No Users Found
                                            </td>
                                        </tr>
                                    ) : null}
                                </tbody>
                            </Table>
                        </div>

                        {/* Pagination */}
                        {userPagination.totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                                <p className="mb-0 fs-14 text-light">
                                    Page {userPagination.page} of {userPagination.totalPages}
                                </p>
                                <CustomPagination
                                    currentPage={userPagination.page}
                                    totalPages={userPagination.totalPages}
                                    onPageChange={handleUserPageChange}
                                />
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-end gap-2">
                    <button
                        className="btn btn-outline-light"
                        onClick={() => {
                            setShowUserModal(false);
                            setSelectedUsers([]);
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        // disabled={selectedUsers.length === 0 || userLoading}
                        onClick={async () => {
                            await requestHandler(
                                async () => await addMembers({
                                    id: userModalData.id,
                                    type: userModalData.type,
                                    userIds: selectedUsers
                                }),
                                setUserLoading,
                                (res) => {
                                    toast.success(res.message || 'Members added successfully');
                                    setShowUserModal(false);
                                    setSelectedUsers([]);
                                    // Refresh the list
                                    if (userModalData.type === 'crew') {
                                        getActivityData(page, paginationValue.limit);
                                    } else {
                                        getCommentData(companyPage, paginationCommentValue.limit);
                                    }
                                },
                                () => { }
                            );
                        }}
                    >
                        {userLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Adding...
                            </>
                        ) : (
                            `Confirm${selectedUsers.length > 0 ? ` (${selectedUsers.length})` : ''}`
                        )}
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default MarketplaceManagement